<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // [{ label, icon, disabled, loading, ariaLabel, onClick }]
  actions: { type: Array, default: () => [] },
  stickyMobile: { type: Boolean, default: false },
  wrapMobile: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
  actionsLabel: { type: String, default: '' },
  trailingLabel: { type: String, default: '' },
});

const { t } = useI18n();

const landmarkLabel = computed(
  () => props.ariaLabel || t('LAIS_ACTION_BAR.LANDMARK')
);
const actionsGroupLabel = computed(
  () => props.actionsLabel || t('LAIS_ACTION_BAR.ACTIONS')
);
const trailingGroupLabel = computed(
  () => props.trailingLabel || t('LAIS_ACTION_BAR.TRAILING')
);

const isActionDisabled = action => Boolean(action?.disabled || action?.loading);

const handleActionClick = action => {
  if (isActionDisabled(action)) return;
  action?.onClick?.();
};
</script>

<template>
  <footer
    role="region"
    class="flex min-w-0 shrink-0 flex-col gap-3 border-t border-lais-border-rule bg-lais-bg-card px-4 py-4 font-redHatText max-md:pl-20 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-10"
    :aria-label="landmarkLabel"
    :class="
      stickyMobile
        ? [
            'sticky bottom-0 z-30 max-h-[48dvh] overflow-y-auto overscroll-contain shadow-lais-pop max-sm:flex-row max-sm:justify-between max-sm:gap-2 max-sm:pl-20 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:static lg:max-h-none lg:overflow-visible lg:shadow-none lg:pb-4',
            wrapMobile
              ? 'max-sm:flex-wrap max-sm:items-start'
              : 'max-sm:items-center',
          ]
        : ''
    "
  >
    <div
      v-if="actions.length || $slots.actions"
      role="group"
      :aria-label="actionsGroupLabel"
      class="flex min-w-0 flex-1 flex-wrap items-center gap-2 lg:w-auto lg:flex-none"
      :class="
        stickyMobile
          ? wrapMobile
            ? 'max-sm:w-full max-sm:min-w-0'
            : 'max-sm:w-auto max-sm:shrink-0'
          : ''
      "
    >
      <slot name="actions">
        <button
          v-for="(action, i) in actions"
          :key="action.id || action.label || i"
          type="button"
          :disabled="isActionDisabled(action)"
          :aria-label="action.ariaLabel || action.label"
          :aria-busy="action.loading ? 'true' : undefined"
          class="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-lais bg-lais-bg-soft px-4 text-lais-label-sm font-semibold text-lais-text-muted transition-colors focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:text-lais-text-body disabled:opacity-100 enabled:text-lais-primary enabled:hover:bg-lais-primary-50 max-sm:max-w-full"
          @click="handleActionClick(action)"
        >
          <span
            v-if="action.loading"
            class="i-lucide-loader-circle size-4 shrink-0 animate-spin"
            aria-hidden="true"
          />
          <span
            v-else-if="action.icon"
            class="size-4 shrink-0"
            :class="action.icon"
            aria-hidden="true"
          />
          <span class="min-w-0 whitespace-nowrap">{{ action.label }}</span>
        </button>
      </slot>
    </div>
    <div
      v-if="$slots.trailing"
      role="group"
      :aria-label="trailingGroupLabel"
      class="flex min-w-0 flex-wrap items-center gap-3 text-lais-label-sm lg:w-auto lg:justify-end"
      :class="
        stickyMobile
          ? wrapMobile
            ? 'max-sm:w-full max-sm:min-w-0 max-sm:justify-end'
            : 'max-sm:w-auto max-sm:min-w-0 max-sm:justify-end'
          : ''
      "
    >
      <slot name="trailing" />
    </div>
  </footer>
</template>
